import { Github, Twitter, MessageCircle, Menu, X } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ChatWidget } from "./ChatWidget.jsx";

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-[--border] bg-[--oil-black]/80 backdrop-blur-md">
      <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 font-bold text-xl group">
          <span className="px-2 py-1 bg-gradient-to-r from-[--brand-frontend] to-[--brand-accent] rounded text-[--oil-black]">
            Plexus
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8 text-sm font-medium">
          <a
            href="/docs"
            className="text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          >
            Docs
          </a>
          <a
            href="/projects"
            className="text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          >
            Projects
          </a>
          <a
            href="/about"
            className="text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          >
            About
          </a>
          <a
            href="https://github.com/mzhub/plexus"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-[--action-primary] text-[--oil-black] rounded-full hover:brightness-110 transition-all font-semibold"
          >
            GitHub
          </a>
        </div>

        <button
          className="md:hidden p-2 text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {menuOpen && (
        <div className="md:hidden border-t border-[--border] bg-[--oil-black] px-6 py-4 space-y-4">
          <a
            href="/docs"
            className="block text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          >
            Docs
          </a>
          <a
            href="/projects"
            className="block text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          >
            Projects
          </a>
          <a
            href="/about"
            className="block text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
          >
            About
          </a>
          <a
            href="https://github.com/mzhub/plexus"
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 bg-[--action-primary] text-[--oil-black] rounded-full text-center hover:brightness-110 transition-all font-semibold"
          >
            GitHub
          </a>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[--border] py-10 mt- bg-[--tungsten]/10">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-2">
            <a
              href="/"
              className="inline-flex items-center gap-2 font-bold text-xl mb-4"
            >
              <span className="px-2 py-1 bg-gradient-to-r from-[--brand-frontend] to-[--brand-accent] rounded text-[--oil-black]">
                Plexus
              </span>
            </a>
            <p className="text-[--text-secondary] max-w-sm">
              The AI-native fullstack framework for React. Build
              production-ready agent-powered applications.
            </p>
          </div>
          <div>
            <h4 className="font-bold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-[--text-secondary]">
              <li>
                <a
                  href="/docs"
                  className="hover:text-[--brand-frontend] transition-colors"
                >
                  Documentation
                </a>
              </li>
              <li>
                <a
                  href="/docs/quick-start"
                  className="hover:text-[--brand-frontend] transition-colors"
                >
                  Quick Start
                </a>
              </li>
              <li>
                <a
                  href="/projects"
                  className="hover:text-[--brand-frontend] transition-colors"
                >
                  Examples
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">Community</h4>
            <ul className="space-y-2 text-sm text-[--text-secondary]">
              <li>
                <a
                  href="https://github.com/mzhub/plexus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[--brand-frontend] transition-colors"
                >
                  GitHub
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="hover:text-[--brand-frontend] transition-colors"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="https://github.com/mzhub/plexus/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[--brand-frontend] transition-colors"
                >
                  Report Issues
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[--border] pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[--text-secondary] text-sm">
            2026 Plexus. Open source under MIT license.
          </p>
          <div className="flex gap-4">
            <a
              href="https://github.com/mzhub/plexus"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
            >
              <Github size={20} />
            </a>
            <a
              href="#"
              className="text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
            >
              <Twitter size={20} />
            </a>
            <a
              href="#"
              className="text-[--text-secondary] hover:text-[--brand-frontend] transition-colors"
            >
              <MessageCircle size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        {children}
      </main>
      {!isHomePage && <Footer />}
      <ChatWidget />
    </div>
  );
}

